// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

/**
 * `_executionTier` — resolves the dashboard's **execution-hygiene** counters
 * (evaluation-run outcomes, meta-operation outcomes, retried LLM calls per
 * phase, exhausted retry budgets) and derives their display rates (PRD
 * §Modules `ExecutionTier`; issue `cycle-head-strip`, US §1–§14).
 *
 * Fallback ladder (per metric): a progress row's field wins; where the
 * progress field is absent (older emitter) the snapshot fills it *only for
 * the metrics with a snapshot home* — the summed per-agent eval rollup and
 * the per-phase call ledgers. Meta-op / retry / exhausted counters are
 * progress-only, so they render "—" on a pre-seam cycle. With neither
 * source, every metric is `null` (INV-3 — absent, never a fabricated 0).
 *
 * Per-phase call denominators are NOT new fields: they come from the
 * `n_calls` on each spend ledger (`evaluation_spend` = eval; `architect_spend
 * + genetic_spend` = meta).
 *
 * Rates are returned only when their denominator > 0 (INV-4). The overall
 * retry rate divides distinct retried calls by the LIVE total calls (both
 * folded on every event), so it is present from the first tick and bounded
 * ≤100% by construction. The per-phase retry rates keep their round-end ledger
 * denominators — the only per-phase denominators that exist — and are clamped
 * to ≤100% so a fresh live numerator over a stale ledger can never read 200%.
 *
 * Pure: already-parsed payloads in, resolved values out — no fetch, no React
 * (INV-5). Reuses `selectEvalRollup` from the cannibalised health engine so a
 * single eval error-rate computation remains in the Frontend (INV-10).
 */

import { isFiniteNumber } from "@/lib/number-guards"
import { selectEvalRollup } from "@/lib/cycle-health"
import type { CycleProgress } from "./_progressPayload"

export interface ExecutionTier {
  /** Evaluation runs attempted (agent-level) — the eval-fail denominator. */
  nEvaluations: number | null
  /** Evaluation runs that ended without a result — the eval-fail numerator. */
  nEvaluationErrors: number | null
  /** Meta operations that completed — part of the meta-fail denominator. */
  metaOpsCompleted: number | null
  /** Meta operations that failed — the meta-fail numerator. */
  metaOpsFailed: number | null
  /** Distinct eval-phase LLM calls with ≥1 retry. */
  nCallsRetriedEval: number | null
  /** Distinct meta-phase LLM calls with ≥1 retry. */
  nCallsRetriedMeta: number | null
  /** Retry budgets fully spent (call-level). */
  nExhausted: number | null
  /** Eval-phase LLM call count (`evaluation_spend.n_calls`) — retry denominator. */
  evalCalls: number | null
  /** Meta-phase LLM call count (`architect_spend + genetic_spend`) — retry denominator. */
  metaCalls: number | null
}

export interface ExecutionRates {
  /** `n_evaluation_errors / n_evaluations` %, or `null` when no evaluations. */
  evalFailPct: number | null
  /** `meta_ops_failed / (completed + failed)` %, or `null` when no meta ops. */
  metaFailPct: number | null
  /** Distinct eval retries / eval calls %, or `null` when no eval calls. */
  evalRetryPct: number | null
  /** Distinct meta retries / meta calls %, or `null` when no meta calls. */
  metaRetryPct: number | null
  /** Distinct retries / the LIVE total calls %, or `null` when the live total is unknown. */
  overallRetryPct: number | null
}

const finiteOrNull = (value: unknown): number | null =>
  isFiniteNumber(value) ? value : null

/** Progress wins; the snapshot fills only where progress lacks the field. */
const resolve = (
  progressValue: number | null,
  snapshotValue: number | null,
): number | null => progressValue ?? snapshotValue

const sumCalls = (
  a: number | null,
  b: number | null,
): number | null => (a == null && b == null ? null : (a ?? 0) + (b ?? 0))

export const selectExecutionTier = (
  progress: CycleProgress | null,
  snapshot: CycleSnapshotDto | null,
): ExecutionTier => {
  // Eval outcomes: progress carries them directly; the snapshot's home is the
  // summed per-agent rollup (the reused health-engine selector).
  const rollup = selectEvalRollup(snapshot)
  const snapEvaluations = rollup?.nEvaluations ?? null
  const snapEvaluationErrors = rollup?.nEvaluationErrors ?? null

  const snapEvalCalls = finiteOrNull(snapshot?.evaluation_spend?.n_calls)
  const snapMetaCalls = sumCalls(
    finiteOrNull(snapshot?.architect_spend?.n_calls),
    finiteOrNull(snapshot?.genetic_spend?.n_calls),
  )

  const progEvalCalls = finiteOrNull(progress?.evaluation_spend?.n_calls)
  const progMetaCalls = sumCalls(
    finiteOrNull(progress?.architect_spend?.n_calls),
    finiteOrNull(progress?.genetic_spend?.n_calls),
  )

  return {
    nEvaluations: resolve(finiteOrNull(progress?.n_evaluations), snapEvaluations),
    nEvaluationErrors: resolve(
      finiteOrNull(progress?.n_evaluation_errors),
      snapEvaluationErrors,
    ),
    // Meta-op / retry / exhausted counters are progress-only (no snapshot home).
    metaOpsCompleted: finiteOrNull(progress?.meta_ops_completed),
    metaOpsFailed: finiteOrNull(progress?.meta_ops_failed),
    nCallsRetriedEval: finiteOrNull(progress?.n_calls_retried_eval),
    nCallsRetriedMeta: finiteOrNull(progress?.n_calls_retried_meta),
    nExhausted: finiteOrNull(progress?.n_exhausted),
    evalCalls: resolve(progEvalCalls, snapEvalCalls),
    metaCalls: resolve(progMetaCalls, snapMetaCalls),
  }
}

/** `numerator / denominator * 100`, only when the denominator is > 0 (INV-4). */
const rate = (
  numerator: number | null,
  denominator: number | null,
): number | null =>
  numerator != null && denominator != null && denominator > 0
    ? (numerator / denominator) * 100
    : null

// The per-phase retry rates divide a live numerator by a round-end ledger
// denominator, so mid-round the fresh numerator can outrun the stale
// denominator; clamp to 100% (INV-4). The overall rate needs no clamp — both
// its legs are live, and retried calls are distinct calls ⊆ the live total.
const clampRate = (value: number | null): number | null =>
  value == null ? null : Math.min(value, 100)

/**
 * `liveTotalCalls` is the authoritative live call count (the accumulating
 * tier's `totalCalls`, folded on every event) — the overall retry
 * denominator. It is NOT the per-phase ledger sum, which lags to round-end and
 * would leave the overall rate at 0/absent through the first round (CORR-06).
 */
export const deriveExecutionRates = (
  tier: ExecutionTier,
  liveTotalCalls: number | null = null,
): ExecutionRates => {
  const metaTotal =
    tier.metaOpsCompleted == null && tier.metaOpsFailed == null
      ? null
      : (tier.metaOpsCompleted ?? 0) + (tier.metaOpsFailed ?? 0)

  const retriedTotal =
    tier.nCallsRetriedEval == null && tier.nCallsRetriedMeta == null
      ? null
      : (tier.nCallsRetriedEval ?? 0) + (tier.nCallsRetriedMeta ?? 0)

  return {
    evalFailPct: rate(tier.nEvaluationErrors, tier.nEvaluations),
    metaFailPct: rate(tier.metaOpsFailed, metaTotal),
    evalRetryPct: clampRate(rate(tier.nCallsRetriedEval, tier.evalCalls)),
    metaRetryPct: clampRate(rate(tier.nCallsRetriedMeta, tier.metaCalls)),
    overallRetryPct: rate(retriedTotal, liveTotalCalls),
  }
}
